<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useRouter } from 'vue-router';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactsAPI from 'dashboard/api/contacts';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const CHANNEL_KEYS = {
  telegram: 'CONTACTS_DUPLICATES.CHANNELS.TELEGRAM',
  max: 'CONTACTS_DUPLICATES.CHANNELS.MAX',
  vk: 'CONTACTS_DUPLICATES.CHANNELS.VK',
  web_widget: 'CONTACTS_DUPLICATES.CHANNELS.WEBSITE',
  email: 'CONTACTS_DUPLICATES.CHANNELS.EMAIL',
  api: 'CONTACTS_DUPLICATES.CHANNELS.API',
  sms: 'CONTACTS_DUPLICATES.CHANNELS.SMS',
  whatsapp: 'CONTACTS_DUPLICATES.CHANNELS.WHATSAPP',
};

const isFetching = ref(false);
const groups = ref([]);
// keep[groupIndex] = contactId chosen as the surviving record
const keep = ref({});
const mergingIndex = ref(null);

const channelLabel = channel =>
  CHANNEL_KEYS[channel] ? t(CHANNEL_KEYS[channel]) : channel;

const confidenceLabel = confidence =>
  confidence === 'high'
    ? t('CONTACTS_DUPLICATES.CONFIDENCE.HIGH')
    : t('CONTACTS_DUPLICATES.CONFIDENCE.MEDIUM');

const reasonLabel = reason =>
  reason === 'social_handle'
    ? t('CONTACTS_DUPLICATES.REASON.SOCIAL_HANDLE')
    : t('CONTACTS_DUPLICATES.REASON.NAME_CROSS_CHANNEL');

const channelsSummary = contact =>
  t('CONTACTS_DUPLICATES.CHANNELS_SUMMARY', {
    value: contact.channels.map(channelLabel).join(', ') || '—',
  });

const formatActivity = ts => (ts ? dynamicTime(ts * 1000) : '—');

const fetchSuggestions = async () => {
  isFetching.value = true;
  try {
    const response = await ContactsAPI.duplicateSuggestions();
    groups.value = response.data.payload || [];
    keep.value = {};
    groups.value.forEach((group, index) => {
      keep.value[index] = group.contacts[0]?.id;
    });
  } catch (error) {
    useAlert(t('CONTACTS_DUPLICATES.API.ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const goBack = () => router.push({ name: 'contacts_dashboard_index' });

const openContact = id =>
  router.push({ name: 'contacts_edit', params: { contactId: id } });

const mergeGroup = async (group, index) => {
  const parentId = keep.value[index];
  const children = group.contacts.filter(c => c.id !== parentId);
  if (!parentId || !children.length) return;

  mergingIndex.value = index;
  try {
    // Fold every duplicate into the surviving record, one at a time.
    // eslint-disable-next-line no-restricted-syntax
    for (const child of children) {
      // eslint-disable-next-line no-await-in-loop
      await store.dispatch('contacts/merge', {
        parentId,
        childId: child.id,
      });
    }
    useAlert(t('CONTACTS_DUPLICATES.MERGE.SUCCESS'));
    await fetchSuggestions();
  } catch (error) {
    useAlert(t('CONTACTS_DUPLICATES.MERGE.ERROR'));
  } finally {
    mergingIndex.value = null;
  }
};

onMounted(() => fetchSuggestions());
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div
          class="flex items-center justify-between w-full py-6 lg:py-0 lg:h-20 gap-4"
        >
          <div class="flex items-center gap-3">
            <Button
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-arrow-left"
              @click="goBack"
            />
            <span class="text-xl font-medium text-n-slate-12">
              {{ t('CONTACTS_DUPLICATES.HEADER') }}
            </span>
          </div>
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-lucide-refresh-cw"
            :label="t('CONTACTS_DUPLICATES.REFRESH')"
            :is-loading="isFetching"
            :disabled="isFetching"
            @click="fetchSuggestions"
          />
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl h-full mx-auto py-4">
        <div
          v-if="isFetching"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>

        <div
          v-else-if="!groups.length"
          class="flex items-center justify-center py-10"
        >
          <span class="text-base text-n-slate-11">
            {{ t('CONTACTS_DUPLICATES.EMPTY') }}
          </span>
        </div>

        <div v-else class="flex flex-col gap-4">
          <p class="text-sm text-n-slate-11">
            {{ t('CONTACTS_DUPLICATES.SUBTITLE') }}
          </p>

          <div
            v-for="(group, index) in groups"
            :key="index"
            class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <div class="flex items-center gap-2">
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded-full"
                  :class="
                    group.confidence === 'high'
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-amber-3 text-n-amber-11'
                  "
                >
                  {{ confidenceLabel(group.confidence) }}
                </span>
                <span class="text-sm text-n-slate-11">
                  {{ reasonLabel(group.reason) }}
                  <span class="text-n-slate-12 font-medium">{{
                    group.match
                  }}</span>
                </span>
              </div>
              <Button
                variant="solid"
                color="blue"
                size="sm"
                icon="i-lucide-merge"
                :label="t('CONTACTS_DUPLICATES.MERGE.BUTTON')"
                :is-loading="mergingIndex === index"
                :disabled="mergingIndex !== null"
                @click="mergeGroup(group, index)"
              />
            </div>

            <div class="flex flex-col divide-y divide-n-weak">
              <label
                v-for="contact in group.contacts"
                :key="contact.id"
                class="flex items-center gap-3 py-2 cursor-pointer"
              >
                <input
                  v-model="keep[index]"
                  type="radio"
                  :value="contact.id"
                  :name="`keep-${index}`"
                  class="accent-n-blue-9"
                />
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-medium text-n-slate-12 truncate">
                      {{ contact.name || t('CONTACTS_DUPLICATES.NO_NAME') }}
                    </span>
                    <span
                      v-if="keep[index] === contact.id"
                      class="text-xs text-n-teal-11"
                    >
                      {{ t('CONTACTS_DUPLICATES.KEEP_HINT') }}
                    </span>
                  </div>
                  <div class="flex flex-wrap gap-x-3 text-xs text-n-slate-11">
                    <span v-if="contact.phone_number">{{
                      contact.phone_number
                    }}</span>
                    <span v-if="contact.email">{{ contact.email }}</span>
                    <span>{{ channelsSummary(contact) }}</span>
                    <span>
                      {{
                        t('CONTACTS_DUPLICATES.CONVERSATIONS', {
                          count: contact.conversations_count,
                        })
                      }}
                    </span>
                    <span>{{ formatActivity(contact.last_activity_at) }}</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  color="slate"
                  size="sm"
                  icon="i-lucide-external-link"
                  @click.prevent="openContact(contact.id)"
                />
              </label>
            </div>
          </div>
        </div>
      </div>
    </main>
  </section>
</template>
